import { useEffect, useState, type ReactNode } from "react";
import homeDiscoverImage from "../assets/company-great-wall.jpg";
import homeAdventureBackgroundImage from "../assets/home-adventure-bg.webp";
import homeAdventureSlideOneImage from "../assets/home-adventure-slide-1.png";
import homeAdventureSlideTwoImage from "../assets/home-adventure-slide-2.webp";

type Slide = {
  id: string;
  eyebrow: string;
  title: string;
  subtitle: string;
  highlights: string[];
  image: string;
};

type HomeHighlight = {
  id: string;
  title: string;
  image: string;
  accentClass: string;
  iconClass: string;
  icon: ReactNode;
};

type HomeDestination = {
  id: string;
  title: string;
  country: string;
  summary: string;
  durationLabel: string;
  price: string;
  oldPrice?: string;
  badge?: string;
  image: string;
};

type HomeWorldwideDestination = {
  id: string;
  title: string;
  subtitle: string;
  image: string;
};

type HomeTabProps = {
  activeIndex: number;
  slides: Slide[];
  heroContent: Slide;
  contacts: string[];
  homeHighlights: HomeHighlight[];
  homeDestinations: HomeDestination[];
  homeDestinationsLoading?: boolean;
  homeWorldwideDestinations: HomeWorldwideDestination[];
  adventureHighlights: string[];
  onMoreInfoClick: () => void;
  homeSearchKeyword: string;
  onHomeSearchKeywordChange: (value: string) => void;
  homeSearchDestination: string;
  homeDestinationOptions: string[];
  onHomeSearchDestinationChange: (value: string) => void;
  homeSearchTopic: string;
  homeTopicOptions: string[];
  onHomeSearchTopicChange: (value: string) => void;
  onHomeSearchSubmit: () => void;
  onHomeDestinationSelect: (id: string) => void;
  onAllServicesClick: () => void;
};

const adventureSlideImages = [
  homeAdventureSlideOneImage,
  homeAdventureSlideTwoImage
];

export default function HomeTab({
  activeIndex,
  slides,
  heroContent,
  contacts,
  homeHighlights,
  homeDestinations,
  homeDestinationsLoading = false,
  homeWorldwideDestinations,
  adventureHighlights,
  onMoreInfoClick,
  homeSearchKeyword,
  onHomeSearchKeywordChange,
  homeSearchDestination,
  homeDestinationOptions,
  onHomeSearchDestinationChange,
  homeSearchTopic,
  homeTopicOptions,
  onHomeSearchTopicChange,
  onHomeSearchSubmit,
  onHomeDestinationSelect,
  onAllServicesClick
}: HomeTabProps) {
  const [adventureSlideIndex, setAdventureSlideIndex] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setAdventureSlideIndex((current) => (current + 1) % adventureSlideImages.length);
    }, 4500);

    return () => {
      window.clearInterval(timer);
    };
  }, []);

  return (
    <>
      <section
        className="relative mx-4 mb-6 min-h-[70vh] overflow-hidden bg-[#0b111a] md:mx-12 md:mb-12 md:min-h-[calc(100vh-96px)]"
      >
        <div className="absolute inset-0">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 bg-cover bg-center transition-opacity duration-700 ${
                index === activeIndex ? "opacity-100" : "opacity-0"
              }`}
              style={{ backgroundImage: `url('${slide.image}')` }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-[rgba(10,18,30,0.18)] via-[rgba(8,13,22,0.32)] to-[rgba(7,10,16,0.45)]" />
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(16,80,170,0.2),transparent_45%)]" />
            </div>
          ))}
        </div>

        <div className="relative z-10 flex min-h-[70vh] flex-col items-center justify-center gap-4 px-6 py-16 text-center text-white md:min-h-[calc(100vh-96px)] md:px-10 lg:px-16">
          <p className="text-4xl font-semibold tracking-[0.08em] text-white/95 md:text-5xl">
            {heroContent.eyebrow}
          </p>
          <h1 className="text-5xl font-semibold leading-[1.05] md:text-7xl lg:text-8xl">
            {heroContent.title}
          </h1>

          <div className="mt-4 flex w-full max-w-md flex-col gap-1 text-[13px] leading-snug text-white/90 md:text-sm">
            {contacts.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className="mx-auto w-full max-w-[1140px] px-4 md:px-8 lg:px-10">
          <div className="max-w-2xl space-y-6">
            <p className="text-xs uppercase tracking-[0.36em] text-[#f7b500]">
              Dream Vacation Destination
            </p>
            <h2 className="text-3xl font-semibold text-[#111827] md:text-5xl">
              Tailored High-End Travel <br className="hidden md:block" />
              For You
            </h2>
            <p className="text-sm leading-relaxed text-[#6b7280] md:text-base">
              Fulcrum Travel creates personalized, high-end journeys for you. We
              offer fully private, tailor-made tours—designed around your
              interests, schedule, and budget—so you can experience China with
              ease, comfort, and unforgettable moments.
            </p>
            <button
              type="button"
              onClick={onMoreInfoClick}
              className="rounded-md bg-[#f7b500] px-6 py-3 text-xs font-semibold text-white shadow-[0_12px_24px_rgba(247,181,0,0.35)]"
            >
              More Info
            </button>
          </div>

          {homeDestinationsLoading ? (
            <div className="mt-12 rounded-2xl border border-[#eef2f6] bg-white px-6 py-10 text-center text-sm text-[#6b7280] shadow-[0_18px_40px_rgba(15,23,36,0.08)]">
              Loading featured trips...
            </div>
          ) : homeDestinations.length === 0 ? (
            <div className="mt-12 rounded-2xl border border-[#eef2f6] bg-white px-6 py-10 text-center text-sm text-[#6b7280] shadow-[0_18px_40px_rgba(15,23,36,0.08)]">
              No featured trips available right now.
            </div>
          ) : (
            <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {homeDestinations.map((item) => (
                <article
                  key={item.id}
                  className="overflow-hidden rounded-2xl border border-[#eef2f6] bg-white shadow-[0_18px_40px_rgba(15,23,36,0.08)]"
                >
                  <div className="relative h-56">
                    <img
                      src={item.image}
                      alt={item.title}
                      className="h-full w-full object-cover"
                    />
                    {item.badge && (
                      <span className="absolute right-4 top-4 rounded-full bg-[#f7b500] px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
                        {item.badge}
                      </span>
                    )}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-md bg-white px-4 py-1 text-[11px] text-[#6b7280] shadow-[0_6px_16px_rgba(15,23,36,0.12)]">
                      {item.durationLabel}
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 px-6 py-6">
                    <div>
                      <h3 className="text-lg font-semibold text-[#111827]">
                        {item.title}
                      </h3>
                      <p className="text-xs text-[#9aa3af]">{item.country}</p>
                    </div>
                    <p className="text-xs leading-relaxed text-[#6b7280]">
                      {item.summary}
                    </p>
                    <div className="mt-3 border-t border-[#eef2f6] pt-4">
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => onHomeDestinationSelect(item.id)}
                          className="rounded bg-[#f7b500] px-4 py-2 text-xs font-semibold text-white"
                        >
                          Details
                        </button>
                        <div className="text-right">
                          <p className="text-[10px] uppercase text-[#9aa3af]">
                            From
                          </p>
                          <p className="text-base font-semibold text-[#111827]">
                            {item.price}{" "}
                            {item.oldPrice && (
                              <span className="text-xs font-normal text-[#9aa3af] line-through">
                                {item.oldPrice}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-[#f5f7fb] py-16">
        <div className="mx-auto w-full max-w-[1140px] px-4 md:px-8 lg:px-10">
          <div className="text-center">
            <p className="text-xs uppercase tracking-[0.36em] text-[#f7b500]">
              Choose your Trip
            </p>
            <h3 className="mt-3 text-3xl font-semibold text-[#111827] md:text-4xl">
              Start your Vacation Now
            </h3>
            <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-[#6b7280]">
              Looking for your dream vacation destination but don't know where
              to start? With the help of experienced and knowledgeable travel
              agents, you can plan the trip of a lifetime with ease.
            </p>
          </div>

          <form
            className="mt-10 rounded-2xl bg-white p-4 shadow-[0_18px_40px_rgba(15,23,36,0.08)] md:p-5"
            onSubmit={(event) => {
              event.preventDefault();
              onHomeSearchSubmit();
            }}
          >
            <div className="grid gap-4 md:grid-cols-[1.2fr_1fr_1fr_220px] md:items-center md:gap-0 md:divide-x md:divide-[#eef2f6]">
              <div className="flex flex-col gap-1 px-4 py-2">
                <span className="text-xs font-semibold text-[#111827]">
                  Search
                </span>
                <input
                  type="text"
                  value={homeSearchKeyword}
                  onChange={(event) =>
                    onHomeSearchKeywordChange(event.target.value)
                  }
                  placeholder="Insert keyword"
                  className="text-sm text-[#6b7280] outline-none placeholder:text-[#9aa3af]"
                />
              </div>
              <div className="flex flex-col gap-1 px-4 py-2">
                <span className="text-xs font-semibold text-[#111827]">
                  Destinations
                </span>
                <select
                  value={homeSearchDestination}
                  onChange={(event) =>
                    onHomeSearchDestinationChange(event.target.value)
                  }
                  className="appearance-none text-sm text-[#6b7280] outline-none"
                >
                  <option value="">All Destinations</option>
                  {homeDestinationOptions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1 px-4 py-2">
                <span className="text-xs font-semibold text-[#111827]">
                  Typologies
                </span>
                <select
                  value={homeSearchTopic}
                  onChange={(event) => onHomeSearchTopicChange(event.target.value)}
                  className="appearance-none text-sm text-[#6b7280] outline-none"
                >
                  <option value="">All Typologies</option>
                  {homeTopicOptions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
              <div className="px-4 py-2">
                <button
                  type="submit"
                  className="w-full rounded-xl bg-[#0d5be1] px-6 py-3 text-xs font-semibold text-white shadow-[0_12px_24px_rgba(13,91,225,0.24)]"
                >
                  SEARCH
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>

      <section className="bg-white py-14">
        <div className="mx-auto w-full max-w-[1140px] px-4 md:px-8 lg:px-10">
          <div className="grid items-center gap-10 lg:grid-cols-[1fr_460px]">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold text-[#f7b500]">Wandering Souls</p>
              <h3 className="mt-3 text-4xl font-semibold leading-tight text-[#111827] md:text-5xl">
                Discover Your Next Adventure
              </h3>
              <p className="mt-5 text-base leading-relaxed text-[#6b7280]">
                Whether you are planning a romantic honeymoon or a family vacation, our
                team will help you find the best destination package at the right
                price. Start your journey with a tailored plan designed around your
                schedule and travel style.
              </p>

              <div className="mt-8 space-y-5">
                <div>
                  <p className="text-sm text-[#6b7280]">Organized Group Tour</p>
                  <div className="mt-2 h-1.5 w-full rounded-full bg-[#e5e7eb]">
                    <div className="h-1.5 w-[90%] rounded-full bg-[#0b1c2b]" />
                  </div>
                </div>
                <div>
                  <p className="text-sm text-[#6b7280]">Single Customized Trip</p>
                  <div className="mt-2 h-1.5 w-full rounded-full bg-[#e5e7eb]">
                    <div className="h-1.5 w-[80%] rounded-full bg-[#0b1c2b]" />
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={onMoreInfoClick}
                className="mt-8 rounded-md bg-[#f7b500] px-6 py-3 text-xs font-semibold text-white shadow-[0_12px_24px_rgba(247,181,0,0.35)]"
              >
                MORE INFO
              </button>
            </div>

            <div className="overflow-hidden rounded-[120px_0_120px_0] shadow-[0_20px_48px_rgba(15,23,36,0.2)]">
              <img
                src={homeDiscoverImage}
                alt="Great Wall adventure"
                className="h-[360px] w-full object-cover md:h-[430px]"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto w-full max-w-[1140px] px-4 md:px-8 lg:px-10">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.36em] text-[#f7b500]">
              Next Adventure
            </p>
            <h3 className="mt-3 text-3xl font-semibold text-[#111827] md:text-4xl">
              Travel Destinations <br className="hidden md:block" />
              Available Worldwide
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-[#6b7280]">
              We have compiled a list of top destinations across the globe,
              scoured the world for the most alluring and fascinating places to
              visit. From the beautiful beaches of the Caribbean to the majestic
              mountains of Europe and the vibrant cities of Asia, our
              destination list has something for everyone.
            </p>
          </div>

          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {homeWorldwideDestinations.map((item) => (
              <article
                key={item.id}
                className="relative overflow-hidden rounded-2xl shadow-[0_18px_40px_rgba(15,23,36,0.16)]"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="h-64 w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-black/70" />
                <span className="absolute right-4 top-4 text-[10px] font-semibold uppercase tracking-[0.24em] text-white/85">
                  All Packages
                </span>
                <div className="absolute bottom-4 left-4 right-4 text-center text-white">
                  <p className="text-sm font-semibold">{item.title}</p>
                  {item.subtitle && (
                    <p className="text-xs text-white/85">{item.subtitle}</p>
                  )}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section
        className="relative overflow-hidden bg-cover bg-center py-16"
        style={{ backgroundImage: `url('${homeAdventureBackgroundImage}')` }}
      >
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(4,9,16,0.75)_0%,rgba(4,9,16,0.65)_45%,rgba(4,9,16,0.35)_100%)]" />
        <div className="relative mx-auto w-full max-w-[1140px] px-4 md:px-8 lg:px-10">
          <div className="grid items-center gap-10 lg:grid-cols-[420px_1fr]">
            <div className="relative overflow-hidden rounded-2xl bg-white/10 p-2 shadow-[0_18px_40px_rgba(0,0,0,0.35)]">
              <img
                src={adventureSlideImages[adventureSlideIndex]}
                alt="Adventure travel"
                className="h-[280px] w-full rounded-xl object-cover md:h-[340px]"
              />
              <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 gap-2">
                {adventureSlideImages.map((_, index) => (
                  <button
                    key={`adventure-slide-dot-${index}`}
                    type="button"
                    aria-label={`Show adventure slide ${index + 1}`}
                    onClick={() => setAdventureSlideIndex(index)}
                    className={`h-2 w-2 rounded-full ${
                      index === adventureSlideIndex ? "bg-white/80" : "bg-white/40"
                    }`}
                  />
                ))}
              </div>
            </div>

            <div className="text-white">
              <p className="text-xs uppercase tracking-[0.36em] text-[#f7b500]">
                Adventure Travel
              </p>
              <h3 className="mt-3 text-3xl font-semibold md:text-4xl">
                Embrace the Thrill of <br className="hidden md:block" />
                the Unknown
              </h3>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/80">
                Are you tired of the typical tourist destinations and looking to
                step out of your comfort zone? Adventure travel may be the
                perfect solution for you! Here are four reasons why you should
                book an adventure travel experience:
              </p>
              <ul className="mt-6 space-y-3 text-sm text-white/85">
                {adventureHighlights.map((item) => (
                  <li key={item} className="flex items-center gap-3">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15 text-[#f7b500]">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3.5 w-3.5"
                      >
                        <path
                          d="M5 12L10 17L19 7"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={onAllServicesClick}
                className="mt-6 rounded-md bg-[#f7b500] px-6 py-3 text-xs font-semibold text-[#1d1d1d] shadow-[0_12px_24px_rgba(247,181,0,0.35)]"
              >
                All Services
              </button>
            </div>
          </div>
        </div>
      </section>

    </>
  );
}
